import type { FC, KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'
import type { Project } from '@/types'

import { useCallback, useRef, useState } from 'react'
import Link from 'next/link'

import { cn } from '@/lib/utils'
import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { LiquidImage } from '@/components/generals/liquid-image'
import { Magnetic } from '@/components/generals/magnetic'
import { Odometer } from '@/components/projects/odometer'

/* Geometry lifted off the original three.js build, measured mid-drag:
   a 16:10 card, siblings pushed out by 1.08 of its width, a scale that falls
   from 1 at the centre to 0.66 by 2.6 units out, and a 58px dip through the
   middle of the arc. */
const SPACING_RATIO = 1.08
const SCALE_FALLOFF = 2.6
const SCALE_MIN = 0.66
const ARC_DIP = 58
const CAPTION_GAP = 15
const VISIBLE_LIMIT = 3.2
const STRIP_RUNS = 5
const WHEEL_THROTTLE = 260
/* Pointer capture is deferred until the drag clears this, because capturing on
   pointerdown retargets the click to the stage and the card link never sees it. */
const DRAG_THRESHOLD = 6

const pad = (value: number): string => {
    return String(value).padStart(2, '0')
}

interface ArcCarouselProps {
    projects: Project[]
    className?: string
}

export const ArcCarousel: FC<ArcCarouselProps> = ({ projects, className }): ReactNode => {
    const count = projects.length

    const rootRef = useRef<HTMLDivElement>(null)
    const stageRef = useRef<HTMLDivElement>(null)
    const stageInnerRef = useRef<HTMLDivElement>(null)
    const stripRef = useRef<HTMLDivElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const slotRefs = useRef<(HTMLDivElement | null)[]>([])
    const cardRefs = useRef<(HTMLAnchorElement | null)[]>([])
    const captionRefs = useRef<(HTMLDivElement | null)[]>([])

    /* The float position lives in a ref and is tweened as a plain object so
       neither React nor the render loop ever wait on a state commit. */
    const motion = useRef({ pos: 0 })
    const geo = useRef({ spacing: 900, cardHeight: 460, runWidth: 0, stripWidth: 0, centers: [] as number[] })
    const drag = useRef({ active: false, captured: false, id: -1, startX: 0, startPos: 0, moved: 0, lastX: 0, lastTime: 0, velocity: 0 })
    const destRef = useRef(0)
    const tweenRef = useRef<gsap.core.Tween | null>(null)
    const directionRef = useRef(1)
    const activeRef = useRef(0)

    const [view, setView] = useState({ index: 0, direction: 1 })

    const tweenTo = useCallback((dest: number, duration = 1.05): void => {
        destRef.current = dest
        tweenRef.current?.kill()

        if (prefersReducedMotion()) {
            motion.current.pos = dest

            return
        }

        tweenRef.current = gsap.to(motion.current, {
            pos: dest,
            duration,
            ease: 'noma',
            overwrite: true
        })
    }, [])

    const step = useCallback((offset: number): void => {
        tweenTo(Math.round(destRef.current) + offset)
    }, [tweenTo])

    /* Jumping to a name always takes the short way round the loop. */
    const goToIndex = useCallback((index: number): void => {
        const from = Math.round(destRef.current)
        let delta = (((index - from) % count) + count) % count

        if (delta > count / 2) {
            delta -= count
        }

        tweenTo(from + delta)
    }, [count, tweenTo])

    useGsap(() => {
        const stage = stageRef.current
        const strip = stripRef.current
        const track = trackRef.current

        if (!stage || !strip || !track) {
            return
        }

        let needsRender = true
        let lastPos = Number.NaN
        let frame = 0
        let lastWheel = 0

        const measure = (): void => {
            const card = cardRefs.current[0]

            if (card && card.offsetWidth > 0) {
                geo.current.spacing = card.offsetWidth * SPACING_RATIO
                geo.current.cardHeight = card.offsetHeight
            }

            const run = track.querySelector<HTMLElement>('[data-strip-run="0"]')

            if (run && run.offsetWidth > 0) {
                const items = Array.from(run.querySelectorAll<HTMLElement>('[data-strip-item]'))

                geo.current.runWidth = run.offsetWidth
                geo.current.centers = items.map((item) => {
                    return item.offsetLeft + item.offsetWidth / 2 - run.offsetLeft
                })
            }

            geo.current.stripWidth = strip.offsetWidth
            needsRender = true
        }

        /* Item centres are interpolated rather than assuming one width, so the
           strip stays locked to the carousel even though the names differ in
           length. */
        const centerOf = (index: number): number => {
            const { centers, runWidth } = geo.current
            const wrapped = ((index % count) + count) % count

            return centers[wrapped] + Math.floor(index / count) * runWidth
        }

        const apply = (): void => {
            const pos = motion.current.pos
            const { spacing, cardHeight, runWidth, stripWidth, centers } = geo.current

            for (let index = 0; index < count; index += 1) {
                const slot = slotRefs.current[index]
                const card = cardRefs.current[index]
                const caption = captionRefs.current[index]

                if (!slot || !card || !caption) {
                    continue
                }

                let offset = (((index - pos) % count) + count) % count

                if (offset > count / 2) {
                    offset -= count
                }

                const distance = Math.abs(offset)

                if (distance > VISIBLE_LIMIT) {
                    slot.style.visibility = 'hidden'

                    continue
                }

                const scale = gsap.utils.mapRange(
                    0,
                    SCALE_FALLOFF,
                    1,
                    SCALE_MIN,
                    Math.min(distance, SCALE_FALLOFF)
                )

                const x = offset * spacing
                const y = ARC_DIP * Math.exp(-(offset * offset) / 1.1)

                slot.style.visibility = 'visible'
                slot.style.zIndex = String(Math.round(100 - distance * 10))
                card.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) scale(${scale.toFixed(4)})`
                card.style.pointerEvents = distance < 0.45 ? 'auto' : 'none'
                caption.style.transform = `translate3d(${x.toFixed(2)}px, ${(y + (cardHeight * scale) / 2 + CAPTION_GAP).toFixed(2)}px, 0)`
                caption.style.opacity = Math.max(0, 1 - distance * 0.8).toFixed(3)
            }

            if (runWidth > 0 && centers.length === count) {
                const floor = Math.floor(pos)
                const fraction = pos - floor
                const center = centerOf(floor) + (centerOf(floor + 1) - centerOf(floor)) * fraction
                const raw = stripWidth / 2 - center
                const wrapped = (((raw % runWidth) + runWidth) % runWidth) - runWidth

                track.style.transform = `translate3d(${wrapped.toFixed(2)}px, 0, 0)`
            }
        }

        const tick = (): void => {
            frame = requestAnimationFrame(tick)

            const pos = motion.current.pos

            if (pos === lastPos && !needsRender) {
                return
            }

            if (!Number.isNaN(lastPos) && Math.abs(pos - lastPos) > 0.0005) {
                directionRef.current = pos > lastPos ? 1 : -1
            }

            lastPos = pos
            needsRender = false
            apply()

            const rounded = ((Math.round(pos) % count) + count) % count

            if (rounded !== activeRef.current) {
                activeRef.current = rounded
                setView({ index: rounded, direction: directionRef.current })
            }
        }

        const onPointerDown = (event: PointerEvent): void => {
            if (event.button !== 0) {
                return
            }

            tweenRef.current?.kill()

            drag.current.active = true
            drag.current.id = event.pointerId
            drag.current.startX = event.clientX
            drag.current.startPos = motion.current.pos
            drag.current.moved = 0
            drag.current.lastX = event.clientX
            drag.current.lastTime = performance.now()
            drag.current.velocity = 0
            drag.current.captured = false

            stage.style.cursor = 'grabbing'
        }

        const onPointerMove = (event: PointerEvent): void => {
            if (!drag.current.active || event.pointerId !== drag.current.id) {
                return
            }

            const dx = event.clientX - drag.current.startX
            const now = performance.now()
            const elapsed = Math.max(1, now - drag.current.lastTime)

            drag.current.moved = Math.max(drag.current.moved, Math.abs(dx))

            /* Now that this is unambiguously a drag rather than a tap, take the
               pointer so the gesture survives leaving the stage. */
            if (!drag.current.captured && drag.current.moved > DRAG_THRESHOLD) {
                drag.current.captured = true
                stage.setPointerCapture(event.pointerId)
            }

            drag.current.velocity = (event.clientX - drag.current.lastX) / elapsed
            drag.current.lastX = event.clientX
            drag.current.lastTime = now

            motion.current.pos = drag.current.startPos - dx / geo.current.spacing
        }

        const onPointerUp = (event: PointerEvent): void => {
            if (!drag.current.active) {
                return
            }

            drag.current.active = false
            drag.current.captured = false
            stage.style.cursor = 'grab'

            if (stage.hasPointerCapture(event.pointerId)) {
                stage.releasePointerCapture(event.pointerId)
            }

            /* A flick carries at most two cards past the release point. */
            const flick = gsap.utils.clamp(-2, 2, (-drag.current.velocity * 240) / geo.current.spacing)

            tweenTo(Math.round(motion.current.pos + flick), 0.95)

            /* `moved` is deliberately NOT cleared here. The click lands after
               pointerup, and guardClick needs it to tell a drag from a tap.
               pointerdown resets it at the start of the next gesture. */
        }

        const onWheel = (event: WheelEvent): void => {
            const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY

            if (Math.abs(delta) < 2) {
                return
            }

            event.preventDefault()

            const now = performance.now()

            if (now - lastWheel < WHEEL_THROTTLE) {
                return
            }

            lastWheel = now
            step(delta > 0 ? 1 : -1)
        }

        stage.addEventListener('pointerdown', onPointerDown)
        stage.addEventListener('pointermove', onPointerMove)
        stage.addEventListener('pointerup', onPointerUp)
        stage.addEventListener('pointercancel', onPointerUp)
        stage.addEventListener('wheel', onWheel, { passive: false })

        const resizeObserver = new ResizeObserver(measure)
        resizeObserver.observe(stage)
        resizeObserver.observe(strip)

        measure()
        apply()
        frame = requestAnimationFrame(tick)

        /* Web fonts land after first layout and change every name width. */
        if (typeof document !== 'undefined' && document.fonts) {
            document.fonts.ready.then(measure).catch(() => undefined)
        }

        if (!prefersReducedMotion()) {
            const intro = gsap.timeline({ delay: 0.35 })

            intro.from(stageInnerRef.current, {
                y: 60,
                opacity: 0,
                scale: 0.9,
                duration: 1.25,
                ease: 'noma'
            }, 0)

            intro.from('[data-strip-inner]', {
                yPercent: 110,
                duration: 1.15,
                ease: 'noma'
            }, 0.28)

            intro.from('[data-counter-row]', {
                opacity: 0,
                y: 20,
                duration: 0.85,
                ease: 'noma'
            }, 0.62)
        }

        return () => {
            cancelAnimationFrame(frame)
            resizeObserver.disconnect()
            tweenRef.current?.kill()
            stage.removeEventListener('pointerdown', onPointerDown)
            stage.removeEventListener('pointermove', onPointerMove)
            stage.removeEventListener('pointerup', onPointerUp)
            stage.removeEventListener('pointercancel', onPointerUp)
            stage.removeEventListener('wheel', onWheel)
        }
    }, rootRef, [count])

    const onStageKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
        if (event.key === 'ArrowRight') {
            event.preventDefault()
            step(1)
        }

        if (event.key === 'ArrowLeft') {
            event.preventDefault()
            step(-1)
        }
    }

    const onNameKeyDown = (event: ReactKeyboardEvent<HTMLSpanElement>, index: number): void => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            goToIndex(index)
        }
    }

    /* A drag that ends on top of the centre card must not follow the link. */
    const guardClick = (event: { preventDefault: () => void }): void => {
        if (drag.current.moved > 6) {
            event.preventDefault()
        }
    }

    return (
        <div ref={rootRef} className={cn('relative flex w-full flex-col', className)}>
            <div
                ref={stageRef}
                role='group'
                aria-roledescription='carousel'
                aria-label='Selected projects, drag or use the arrow keys'
                tabIndex={0}
                onKeyDown={onStageKeyDown}
                data-cursor='drag'
                data-cursor-label='Drag'
                className='relative min-h-0 flex-1 cursor-grab touch-pan-y select-none overflow-hidden outline-offset-[-4px]'
            >
                <div ref={stageInnerRef} className='absolute inset-0'>
                    {projects.map((project, index) => {
                        const isActive = index === view.index

                        return (
                            <div
                                key={project.slug}
                                ref={(node) => {
                                    slotRefs.current[index] = node
                                }}
                                className='pointer-events-none absolute inset-0'
                            >
                                <div className='absolute inset-0 flex items-center justify-center'>
                                    <Link
                                        href={`/projects/${project.slug}`}
                                        ref={(node) => {
                                            cardRefs.current[index] = node
                                        }}
                                        onClick={guardClick}
                                        onDragStart={(event) => event.preventDefault()}
                                        tabIndex={isActive ? 0 : -1}
                                        aria-hidden={isActive ? undefined : 'true'}
                                        aria-label={`${project.title}, ${project.industry}`}
                                        data-cursor='view'
                                        data-cursor-label='View'
                                        className='will-anim pointer-events-none relative block w-[min(78vw,64dvh,520px)] overflow-hidden rounded-[var(--radius-frame)] bg-[#e8e8e4] aspect-[16/10] lg:w-[min(42vw,92dvh,760px)]'
                                    >
                                        {project.video
                                            ? (
                                                <video
                                                    src={project.video}
                                                    poster={project.image}
                                                    autoPlay
                                                    muted
                                                    loop
                                                    playsInline
                                                    preload='metadata'
                                                    aria-hidden='true'
                                                    className='absolute inset-0 size-full object-cover'
                                                />
                                            )
                                            : (
                                                <LiquidImage
                                                    sources={[project.image]}
                                                    alt={`${project.title} project cover`}
                                                    intensity={0.8}
                                                    priority={index < 2}
                                                    className='absolute inset-0 size-full'
                                                />
                                            )}
                                    </Link>
                                </div>

                                <div className='pointer-events-none absolute inset-x-0 top-1/2 flex justify-center'>
                                    <div
                                        ref={(node) => {
                                            captionRefs.current[index] = node
                                        }}
                                        aria-hidden='true'
                                        className='will-anim flex items-center gap-3 whitespace-nowrap opacity-0'
                                    >
                                        <span className='t-mono text-ink'>
                                            {project.industry}
                                        </span>

                                        <span className='block h-px w-6 bg-ink/25' />

                                        <span className='t-mono text-ash'>
                                            {project.year}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>

            <div className='relative z-10 shrink-0'>
                <div ref={stripRef} className='relative w-full overflow-hidden'>
                    <div data-strip-inner>
                        <div
                            ref={trackRef}
                            className='will-anim flex w-max select-none whitespace-nowrap'
                        >
                            {Array.from({ length: STRIP_RUNS }).map((_, run) => (
                                <div
                                    key={`run-${run}`}
                                    data-strip-run={run}
                                    aria-hidden={run === 0 ? undefined : 'true'}
                                    className='flex shrink-0 items-end'
                                >
                                    {projects.map((project, index) => (
                                        <span
                                            key={`${run}-${project.slug}`}
                                            data-strip-item
                                            role={run === 0 ? 'button' : undefined}
                                            tabIndex={run === 0 ? 0 : -1}
                                            onClick={() => goToIndex(index)}
                                            onKeyDown={(event) => onNameKeyDown(event, index)}
                                            className={cn(
                                                't-display block cursor-pointer px-[0.14em] text-[clamp(34px,11vw,52px)] leading-[0.9] tracking-[-0.02em] transition-colors duration-[320ms] lg:text-[clamp(46px,4.17vw,80px)]',
                                                index === view.index ? 'text-ink' : 'text-[#c9c9c4]'
                                            )}
                                        >
                                            {project.title}
                                        </span>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div
                    data-counter-row
                    className='t-cond flex items-center justify-center gap-[18px] pb-6 pt-4 text-[clamp(13px,0.78vw,15px)] leading-none text-ink lg:pb-9'
                >
                    <Magnetic strength={0.3}>
                        <button
                            type='button'
                            onClick={() => step(-1)}
                            aria-label='Previous project'
                            data-cursor='link'
                            className='flex size-7 items-center justify-center rounded-full border border-ink/15 transition-colors duration-500 hover:border-ink/40 hover:text-brand'
                        >
                            <span data-magnetic-inner className='block'>
                                <svg viewBox='0 0 24 24' className='size-3' fill='none' aria-hidden='true'>
                                    <path d='M15 4L7 12l8 8' stroke='currentColor' strokeWidth='2' />
                                </svg>
                            </span>
                        </button>
                    </Magnetic>

                    <span className='flex items-center tabular-nums'>
                        <Odometer value={pad(view.index + 1)} direction={view.direction} />

                        <span aria-hidden='true' className='px-2 opacity-50'>
                            /
                        </span>

                        <span className='sr-only'>
                            of
                        </span>

                        <span>
                            {pad(count)}
                        </span>
                    </span>

                    <Magnetic strength={0.3}>
                        <button
                            type='button'
                            onClick={() => step(1)}
                            aria-label='Next project'
                            data-cursor='link'
                            className='flex size-7 items-center justify-center rounded-full border border-ink/15 transition-colors duration-500 hover:border-ink/40 hover:text-brand'
                        >
                            <span data-magnetic-inner className='block'>
                                <svg viewBox='0 0 24 24' className='size-3' fill='none' aria-hidden='true'>
                                    <path d='M9 4l8 8-8 8' stroke='currentColor' strokeWidth='2' />
                                </svg>
                            </span>
                        </button>
                    </Magnetic>
                </div>
            </div>
        </div>
    )
}
